import { useState, useEffect, useRef } from 'react';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { countries, states, cities } from '../utils/locationData';

interface LocationSelectorProps {
  country: string;
  state: string;
  city: string;
  onCountryChange: (value: string) => void;
  onStateChange: (value: string) => void;
  onCityChange: (value: string) => void;
  errors?: {
    country?: string;
    state?: string;
    city?: string;
  };
  disabled?: boolean;
}

export default function LocationSelector({
  country,
  state,
  city,
  onCountryChange,
  onStateChange,
  onCityChange,
  errors,
  disabled = false,
}: LocationSelectorProps) {
  const [availableStates, setAvailableStates] = useState<any[]>([]);
  const [availableCities, setAvailableCities] = useState<any[]>([]);
  const isInitialLoad = useRef(true);
  const previousCountry = useRef(country);
  const previousState = useRef(state);

  // Initialize available states and cities on first load
  useEffect(() => {
    if (isInitialLoad.current && country) {
      const countryData = countries.find(c => c.name === country);
      if (countryData) {
        setAvailableStates(states[countryData.code] || []);
      }
      
      if (state) {
        const stateCities = cities[state] || [];
        setAvailableCities(stateCities);
      }
      
      isInitialLoad.current = false;
      previousCountry.current = country;
      previousState.current = state;
    }
  }, [country, state]);

  // Update states when country changes (only clear if user changed it)
  useEffect(() => {
    if (!isInitialLoad.current && country && country !== previousCountry.current) {
      const countryData = countries.find(c => c.name === country);
      if (countryData) {
        setAvailableStates(states[countryData.code] || []);
      }
      // Only clear state and city if country actually changed
      onStateChange('');
      onCityChange('');
      setAvailableCities([]);
      previousCountry.current = country;
    }
  }, [country, onCountryChange, onStateChange, onCityChange]);

  // Update cities when state changes
  useEffect(() => {
    if (state) {
      const stateCities = cities[state] || [];
      setAvailableCities(stateCities);
      
      // Only clear city if state actually changed AND city is not valid for the new state
      if (!isInitialLoad.current && state !== previousState.current) {
        // Check if current city exists in the new state's cities
        const isCityValid = stateCities.some(c => c === city);
        if (!isCityValid) {
          onCityChange('');
        }
      }
      previousState.current = state;
    } else {
      setAvailableCities([]);
    }
  }, [state, city, onCityChange]);

  return (
    <div className="space-y-4">
      {/* Country Dropdown - REQUIRED */}
      <div>
        <Label className="mb-2 block">Country *</Label>
        <Select value={country} onValueChange={onCountryChange} disabled={disabled}>
          <SelectTrigger className={errors?.country ? 'border-red-500' : ''}>
            <SelectValue placeholder="Select country" />
          </SelectTrigger>
          <SelectContent>
            {countries.map((c) => (
              <SelectItem key={c.code} value={c.name}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors?.country && (
          <p className="text-xs text-red-500 mt-1">{errors.country}</p>
        )}
      </div>

      {/* State Dropdown - Shows after country selection */}
      {country && availableStates.length > 0 && (
        <div>
          <Label className="mb-2 block">State / Province *</Label>
          <Select value={state} onValueChange={onStateChange} disabled={disabled}>
            <SelectTrigger className={errors?.state ? 'border-red-500' : ''}>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {availableStates.map((s) => (
                <SelectItem key={s.name} value={s.name}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors?.state && (
            <p className="text-xs text-red-500 mt-1">{errors.state}</p>
          )}
        </div>
      )}

      {/* City Dropdown - Shows after state selection */}
      {state && availableCities.length > 0 && (
        <div>
          <Label className="mb-2 block">City *</Label>
          <Select value={city} onValueChange={onCityChange} disabled={disabled}>
            <SelectTrigger className={errors?.city ? 'border-red-500' : ''}>
              <SelectValue placeholder="Select city" />
            </SelectTrigger>
            <SelectContent>
              {availableCities.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors?.city && (
            <p className="text-xs text-red-500 mt-1">{errors.city}</p>
          )}
        </div>
      )}
    </div>
  );
}